import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, deleteField, doc, onSnapshot, Timestamp, updateDoc } from 'firebase/firestore';
import { X } from 'lucide-react';
import { db } from '../lib/firebase';
import { useAuth } from '../lib/auth';
import { DEMO_AIRCRAFT, DEMO_BOOKINGS, DEMO_SQUAWKS } from '../lib/demoData';
import type { AircraftDoc, BookingDoc, SquawkDoc } from '../lib/types';
import { evaluateBooking } from '../lib/bookingRules';
import { fromDateAndTime, localDatePart, localTimePart } from '../lib/format';

const DAY_MS = 86400000;
const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Reschedule an existing reservation. `booking.bookingId` must be the Firestore
// doc id — the `bookingId` field baked into older docs is NOT the doc id, and
// matching on it would make the booking collide with itself in the conflict check.
export function BookingEditModal({
  booking,
  onClose,
  onSaved,
  onCancel,
}: {
  booking: BookingDoc;
  onClose: () => void;
  onSaved?: (patch: Pick<BookingDoc, 'startDateTime' | 'endDateTime' | 'expectedDestinations' | 'notes'>) => void;
  // Hands off to the page's own cancel flow (confirm for a pilot, reason gate for
  // the owner). The modal closes first so the two dialogs never stack.
  onCancel?: () => void;
}) {
  const { profile, demoMode, isAdmin } = useAuth();
  const [aircraft, setAircraft] = useState<AircraftDoc[]>([]);
  const [bookings, setBookings] = useState<BookingDoc[]>([]);
  const [squawks, setSquawks] = useState<SquawkDoc[]>([]);

  // The time inputs snap to the half hour, so a booking stored at an odd minute
  // (11:58) would seed as 11:30. Remember what we seeded: a half the pilot never
  // touched keeps its ORIGINAL timestamp on save instead of the snapped one.
  const seeded = useRef({
    startDate: localDatePart(booking.startDateTime.toDate()),
    startTime: localTimePart(booking.startDateTime.toDate()),
    endDate: localDatePart(booking.endDateTime.toDate()),
    endTime: localTimePart(booking.endDateTime.toDate()),
  }).current;

  const [startDate, setStartDate] = useState(seeded.startDate);
  const [startTime, setStartTime] = useState(seeded.startTime);
  const [endDate, setEndDate] = useState(seeded.endDate);
  const [endTime, setEndTime] = useState(seeded.endTime);
  const [destinations, setDestinations] = useState(booking.expectedDestinations ?? '');
  const [notes, setNotes] = useState(booking.notes ?? '');
  // Owner-only: flip this slot into a red maintenance hold (or back). Same field the
  // booking form writes, so it renders and blocks exactly like a hold created there.
  // Editing a booking fires no Cloud Function, so this never emails or texts anyone.
  const [maintOn, setMaintOn] = useState(Boolean(booking.maintenanceLabel));
  const [maintText, setMaintText] = useState(booking.maintenanceLabel ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The schedule has to be read whole — a pilot's own bookings alone can't tell
  // them whether the new slot lands on top of somebody else's.
  useEffect(() => {
    if (demoMode) {
      setAircraft(DEMO_AIRCRAFT);
      setBookings(DEMO_BOOKINGS);
      setSquawks(DEMO_SQUAWKS);
      return;
    }
    if (!db) return;
    const unsubA = onSnapshot(collection(db, 'aircraft'), (snap) =>
      setAircraft(snap.docs.map((d) => d.data() as AircraftDoc))
    );
    const unsubB = onSnapshot(collection(db, 'bookings'), (snap) =>
      setBookings(snap.docs.map((d) => ({ ...(d.data() as BookingDoc), bookingId: d.id })))
    );
    const unsubS = onSnapshot(collection(db, 'squawks'), (snap) =>
      setSquawks(snap.docs.map((d) => d.data() as SquawkDoc))
    );
    return () => {
      unsubA();
      unsubB();
      unsubS();
    };
  }, [demoMode]);

  const ac = aircraft.find((a) => a.aircraftId === booking.aircraftId) ?? aircraft[0];
  const grounded =
    Boolean(ac?.groundedByOwner) ||
    squawks.some((s) => s.aircraftId === ac?.aircraftId && s.isGrounding && s.status !== 'resolved');

  // Moving the start day drags the end day with it, so a 9a–3p flight stays
  // 9a–3p (and an overnight stays an overnight) when you just change the date.
  function changeStartDate(next: string) {
    const prev = fromDateAndTime(startDate, '00:00');
    const now = fromDateAndTime(next, '00:00');
    const cur = fromDateAndTime(endDate, '00:00');
    setStartDate(next);
    if (prev && now && cur) {
      const shifted = new Date(cur.getTime() + (midnight(now) - midnight(prev)));
      setEndDate(localDatePart(shifted));
    }
  }

  const startTouched = startDate !== seeded.startDate || startTime !== seeded.startTime;
  const endTouched = endDate !== seeded.endDate || endTime !== seeded.endTime;
  const start = startTouched ? fromDateAndTime(startDate, startTime) : booking.startDateTime.toDate();
  const end = endTouched ? fromDateAndTime(endDate, endTime) : booking.endDateTime.toDate();
  const durationHours = start && end ? (end.getTime() - start.getTime()) / 3600000 : 0;
  const unchanged =
    !startTouched &&
    !endTouched &&
    destinations === (booking.expectedDestinations ?? '') &&
    notes === (booking.notes ?? '') &&
    maintOn === Boolean(booking.maintenanceLabel) &&
    (!maintOn || maintText.trim() === (booking.maintenanceLabel ?? ''));

  const evaluation = useMemo(() => {
    if (!ac || !profile || !start || !end) return null;
    return evaluateBooking({
      start,
      end,
      aircraft: ac,
      lessee: profile,
      // Exclude this booking from the conflict check — otherwise it always
      // conflicts with itself and nothing can ever be rescheduled.
      existingBookings: bookings.filter((b) => b.bookingId !== booking.bookingId),
      grounded,
      // Only when the start is untouched — extending a flight that's already
      // underway is the whole point; moving one into the past never is.
      keepingStart: !startTouched,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ac, profile, startDate, startTime, endDate, endTime, bookings, grounded, booking.bookingId]);

  const blocked = evaluation?.outcome === 'reject';

  async function save() {
    if (!start || !end || blocked || unchanged) return;
    setSaving(true);
    setError(null);
    const patch = {
      startDateTime: Timestamp.fromDate(start),
      endDateTime: Timestamp.fromDate(end),
      expectedDestinations: destinations,
      notes,
    };
    const label = maintText.trim() || 'Maintenance';
    try {
      if (demoMode) {
        // Mutate the shared sample object so the list and calendar both reflect it.
        Object.assign(booking, patch);
        if (isAdmin) {
          if (maintOn) booking.maintenanceLabel = label;
          else delete booking.maintenanceLabel;
        }
      } else {
        if (!db) throw new Error('Not connected.');
        const maintPatch = !isAdmin
          ? {}
          : maintOn
            ? { maintenanceLabel: label }
            : booking.maintenanceLabel
              ? { maintenanceLabel: deleteField() }
              : {};
        await updateDoc(doc(db, 'bookings', booking.bookingId), { ...patch, ...maintPatch });
      }
      onSaved?.(patch);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not save the change. Please try again.'
      );
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy-900/40 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <div
        className="card max-h-[92vh] w-full max-w-md overflow-y-auto rounded-b-none p-5 sm:rounded-2xl sm:p-6"
        style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold text-navy-900">Change booking</h3>
            <p className="mt-0.5 text-sm text-ink-muted">
              Move the times or update the details. Your slot stays reserved while you edit.
            </p>
          </div>
          <button onClick={onClose} aria-label="Close" className="btn-ghost -mr-2 -mt-1 min-h-0 p-2">
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <div>
            <label className="label">Starts</label>
            <div className="flex gap-2">
              <input
                type="date"
                aria-label="Start date"
                className="input min-w-0 flex-1 cursor-pointer bg-sky-50/60 px-3 font-medium text-navy-900"
                value={startDate}
                onChange={(e) => changeStartDate(e.target.value)}
              />
              <input
                type="time"
                step={1800}
                aria-label="Start time"
                className="input w-[44%] shrink-0 cursor-pointer bg-sky-50/60 px-3 font-medium text-navy-900"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="label">Ends</label>
            <div className="flex gap-2">
              <input
                type="date"
                aria-label="End date"
                className="input min-w-0 flex-1 cursor-pointer bg-sky-50/60 px-3 font-medium text-navy-900"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <input
                type="time"
                step={1800}
                aria-label="End time"
                className="input w-[44%] shrink-0 cursor-pointer bg-sky-50/60 px-3 font-medium text-navy-900"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
            {durationHours > 0 && (
              <p className="mt-1.5 text-xs text-ink-muted">
                {durationHours % 1 === 0 ? durationHours : durationHours.toFixed(1)} hr block
                {end && start && end.getTime() - start.getTime() > DAY_MS ? ' · multi-day' : ''}
              </p>
            )}
          </div>

          <div>
            <label className="label">Expected destinations (optional)</label>
            <input
              className="input"
              placeholder="KLGB → KCMA → KLGB"
              value={destinations}
              onChange={(e) => setDestinations(e.target.value)}
            />
          </div>

          <div>
            <label className="label">Notes (optional)</label>
            <textarea className="input-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {isAdmin && (
            <div className="rounded-xl border border-red-200 bg-red-50/60 p-4">
              <label className="flex items-center gap-2 text-sm font-medium text-red-900">
                <input type="checkbox" checked={maintOn} onChange={(e) => setMaintOn(e.target.checked)} />
                Maintenance hold (owner only) — shows red on every calendar
              </label>
              {maintOn && (
                <div className="mt-3">
                  <label className="label">What’s being done</label>
                  <input
                    className="input"
                    value={maintText}
                    onChange={(e) => setMaintText(e.target.value)}
                    placeholder="e.g. Avionics — GPS antenna check"
                    autoFocus={!booking.maintenanceLabel}
                  />
                  <p className="mt-2 text-xs text-red-800">
                    Keeps this slot, replaces the flight with a red maintenance block carrying this
                    description. Nobody is notified.
                  </p>
                </div>
              )}
            </div>
          )}

          {evaluation && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm ${
                blocked
                  ? 'border-red-200 bg-red-50 text-red-800'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-900'
              }`}
            >
              <div className="font-medium">{blocked ? 'Can’t move it there' : 'Ready to save'}</div>
              <ul className="mt-1 list-disc pl-5">
                {evaluation.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">
            Never mind
          </button>
          <button
            onClick={save}
            disabled={saving || blocked || unchanged || !start || !end}
            className="btn-primary flex-1"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        {onCancel && (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => {
                onClose();
                onCancel();
              }}
              disabled={saving}
              className="text-sm font-medium text-red-600 hover:underline"
            >
              Cancel this booking
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
